import { useEffect, useRef, useState } from "react"
import Form, { useForm } from "../../gui/Form/Form"
import FormControl from "../../gui/Form/FormControl"
import Validators from "../../gui/Form/Validators"
import { useOverlay } from "../../gui/Overlay/OverlayProvider"
import Holding from "../../models/Holding"
import { usePath } from "../../libs/path/PathProvider"
import AccountSelect from "../widgets/AccountSelect"
import Button from "../../gui/Button/Button"
import TextInput, { Formatters, ITextInput } from "../../gui/TextInput/TextInput"
import DatePicker, { IDatePicker } from "../../gui/Calendar/DatePicker"
import TransTypeSelect from "../widgets/TransTypeSelect"
import Checkbox, { IToggleButton } from "../../gui/Button/Checkbox"
import { IDropdown } from "../../gui/Dropdown/Dropdown"
import usePortfolio from "../../hooks/PortfolioProvider"
import PortAccount from "../../models/PortAccount"
import { useSnackbar } from "../../gui/Snackbar/SnackbarProvider"
import { ETransactionType } from "../../shared/ITransaction"
import PortUtils from "../../portlibs/PortUtils"
import { TAddHoldingProps } from "./TAddHoldingProps"

export default function DividendDlg(props:TAddHoldingProps){
    const {transaction} = props
    const [busy,setBusy] = useState<boolean>(false)
    const refAction = useRef<IDropdown<ETransactionType>|null>(null)
    const refQty = useRef<ITextInput|null>(null)
    const refAccount = useRef<IDropdown<number>|null>(null)
    const refDate = useRef<IDatePicker|null>(null)
    const refNote = useRef<ITextInput|null>(null)
    const refUpdateCash = useRef<IToggleButton|null>(null)
    const refTax = useRef<IToggleButton|null>(null)
    const {popOverlay} = useOverlay()
    const {valid} = useForm()
    const {portfolio} = usePortfolio()
    const {query} = usePath()
    const {account} = PortUtils.getFilter(query, 0)

    const holding = transaction?.holding ?? (props.holdingId ? portfolio.holding(props.holdingId)?.filterByAccount(account) : undefined)
    const ticker = transaction?.holding.id ?? holding!.id
    const title = (transaction?.holding.name ?? holding!.name ) + ` ${ticker}`
    const {addSnackbar} = useSnackbar()

    const onSave = async () => {
        if (busy) return
        else setBusy(false)

        const trans = Holding.cloneTransaction(props.transaction, holding, ticker)
        trans.accountId = refAccount.current!.value!
        trans.price = 1
        trans.quantity = parseFloat(refQty.current!.value!)
        trans.date = trans.maturity = refDate.current!.value!
        trans.yield = 0
        trans.note = refNote.current!.value!
        trans.type = refAction.current!.value!
        try {
            await PortAccount.i.update(portfolio, trans, !!refUpdateCash.current?.checked, !!refTax.current?.checked)
            popOverlay(PortUtils.AddHoldingId)
        }catch(e){
            addSnackbar(e)
        }finally{
            setBusy(false)
        }
    }

    useEffect(() => {
        
    }, [])

    const payout = transaction ? transaction.quantity.toString() : (holding ? holding.distribution.toFixed(3) : "") 
    return <>
        <h2 className="title">{title}</h2>
        <div className="content">
            <div className="grid gap-16">
                <FormControl validators={[Validators.Required("Account")]}>
                    <AccountSelect ref={refAccount} defaultValue={transaction?.accountId ?? (account < 0 ? undefined : account)}/>
                </FormControl>
                <TransTypeSelect ref={refAction} filter="dividend" defaultValue={transaction?.type ?? ETransactionType.dividend}/>
                <div className="flex gap-8">
                    <Checkbox ref={refUpdateCash}>Cash</Checkbox>
                    <Checkbox ref={refTax}>Tax</Checkbox>
                </div>
            </div>
            <div className="grid gap-16">
                <FormControl validators={[Validators.NonZeroNumber("Quantity")]}>
                    <TextInput ref={refQty} placeholder="Quantity" float={true} evaluator={Formatters.Evaluate} defaultValue={payout}/>
                </FormControl>
                <FormControl validators={[Validators.Required("Date")]}>
                    <DatePicker ref={refDate} placeholder="Date" float={true} defaultValue={props.transaction?.date ?? new Date()}/>
                </FormControl>
            </div>
            <TextInput ref={refNote} placeholder="Notes" float={true} defaultValue={props.transaction?.note}/>
        </div>
        <div className="actions">
            <Button onClick={_ => popOverlay(PortUtils.AddHoldingId)}>Cancel</Button>
            <Button variant="contained" onClick={onSave} disabled={!valid||busy}>Save</Button>
        </div>
    </>
}

export function useDividendDlg(){
    const {addOverlay,hasOverlay} = useOverlay()
    const {hash,setHash} = usePath()
    const {portfolio} = usePortfolio()

    const afterClosed = () => {
        const {holdingId} = PortUtils.parseHash(hash)
        setHash(PortUtils.createHash(holdingId))
    }
    const addDividend = (props:TAddHoldingProps) => {
        const holding = props.holdingId ? portfolio.holding(props.holdingId) : null
        if (hasOverlay(PortUtils.AddHoldingId) || (!holding && !props.transaction)) return
        addOverlay(PortUtils.AddHoldingId, {
            children:<Form><DividendDlg {...props}/></Form>,
            className:"dialog",
            afterClosed
        })
    }
    return {addDividend}
}